import { isChurchNavPathActive, type ChurchNavSection } from "@/features/dashboard/model/church-nav"

/**
 * The phone's navigation model, in one place: which sections are bottom tabs, where each tab lands, and therefore
 * which screens are ROOT screens (the tab landings, with the big app header) and which are STACK screens (everything
 * else, with a back button and their own title bar).
 */

export const MOBILE_TAB_COUNT = 4

/**
 * The bottom tabs' own order, independent of the menu's: the first four sections this user can open win. Finance
 * outranks Departments (daily work for treasurers and pastors); Departments, Budgets, Assets and Forms only take a
 * tab when someone lacks one of the sections above them, and are always in the Menu.
 */
const MOBILE_TAB_PRIORITY = ["/", "/members", "/finance", "/activities", "/departments", "/budgets", "/assets", "/forms"]

/**
 * Where a bottom tab lands when it differs from the menu link. Members opens its overview first on a phone (as
 * Finance does); the list is one tap away from there. The desktop sidebar keeps opening the list.
 */
const MOBILE_TAB_LANDING: Record<string, string> = { "/members": "/members" }

export function mobileTabLanding(section: { url: string; primaryUrl?: string }): string {
  return MOBILE_TAB_LANDING[section.url] ?? section.primaryUrl ?? section.url
}

function mobileTabRank(url: string): number {
  const rank = MOBILE_TAB_PRIORITY.indexOf(url)
  return rank === -1 ? MOBILE_TAB_PRIORITY.length : rank
}

/** The sections that are bottom tabs for this user, in tab order (at most {@link MOBILE_TAB_COUNT}). */
export function selectMobilePrimaryTabs(sections: ChurchNavSection[]): ChurchNavSection[] {
  return sections
    .filter((section) => section.mobilePrimaryTab)
    .sort((left, right) => mobileTabRank(left.url) - mobileTabRank(right.url))
    .slice(0, MOBILE_TAB_COUNT)
}

/** A root screen is exactly a tab's landing page; a tab's other pages (its lists, its records) are stack screens. */
export function isMobileRootPath(pathname: string, tabs: ChurchNavSection[]): boolean {
  return tabs.some((tab) => isChurchNavPathActive(pathname, mobileTabLanding(tab), "exact"))
}

/**
 * The landing of the tab a stack screen belongs to (Transactions belongs to Finance), or null when it belongs to
 * none (settings, reports reached from the menu). Where back goes when there is no history to go back to.
 */
export function resolveMobileSectionRoot(pathname: string, tabs: ChurchNavSection[]): string | null {
  // "/" is every path's prefix, so Home only owns the paths no other tab claims.
  const owner = tabs.find((tab) => tab.url !== "/" && isChurchNavPathActive(pathname, tab.url, tab.match))
  return owner ? mobileTabLanding(owner) : null
}
